(() => {
  if (customElements.get('maxler-purchase-options')) return;

  class MaxlerPurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.optionsRoot = this.querySelector('[data-options]');
      this.fieldset = this.querySelector('fieldset');
      this.planInput = this.querySelector('[data-selling-plan-input]');
      this.status = this.querySelector('[data-status]');
      this.variantData = new Map(JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]').map(variant => [String(variant.id), variant]));
      this.currentVariantId = String(this.dataset.variantId || '');
      this.form = document.getElementById(this.dataset.formId);
      this.currentPlanId = String(this.planInput?.value || '');
      this.addButton = this.form?.querySelector('[name="add"]');
      this.section = this.closest('[data-section-type="product"]');
      if (!this.optionsRoot || !this.fieldset || !this.planInput || !this.form) return;

      this.optionsRoot.addEventListener('change', event => this.onPurchaseOptionChange(event), {signal: this.signal});
      this.section?.addEventListener('variantChange', event => {
        if (event.detail && Object.prototype.hasOwnProperty.call(event.detail, 'variant')) {
          this.onVariantChange(event.detail.variant);
          queueMicrotask(() => { if (!this.signal.aborted) this.syncFormPlan(); });
        }
      }, {signal: this.signal});
      this.form.addEventListener('submit', event => this.onSubmit(event), {capture: true, signal: this.signal});
      this.render(this.currentVariantId, {preservePlanId: this.currentPlanId});
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    onVariantChange(variant) {
      if (!variant) {
        this.currentVariantId = '';
        this.currentPlanId = '';
        this.dataset.variantId = '';
        this.fieldset.disabled = true;
        this.requiredPlanMissing = true;
        this.planInput.value = '';
        this.status.textContent = this.dataset.unavailableText || '';
        this.syncFormPlan();
        return;
      }
      const nextId = String(variant.id);
      const previousPlanId = this.currentPlanId;
      this.render(nextId, {preservePlanId: previousPlanId});
    }

    onPurchaseOptionChange(event) {
      const input = event.target.closest('input[data-purchase-mode]');
      if (!input || !this.optionsRoot.contains(input) || input.disabled || this.fieldset.disabled) return;
      this.currentPlanId = input.value === 'subscription' ? String(input.dataset.planId || '') : '';
      const variant = this.variantData.get(this.currentVariantId);
      this.requiredPlanMissing = Boolean(variant?.requires_selling_plan && !this.currentPlanId);
      this.syncFormPlan();
      this.updateSelectedStyles();
    }

    render(variantId, {preservePlanId = ''} = {}) {
      const variant = this.variantData.get(String(variantId));
      this.currentVariantId = String(variantId);
      this.currentPlanId = '';
      this.optionsRoot.replaceChildren();
      this.dataset.variantId = this.currentVariantId;
      this.fieldset.disabled = !variant || !variant.available;
      this.planInput.value = '';
      this.requiredPlanMissing = false;

      if (!variant || !variant.available) {
        if (!variant) {
          this.status.textContent = this.dataset.unavailableText || '';
          this.requiredPlanMissing = true;
          this.syncFormPlan();
          return;
        }
      }

      const allocations = Array.isArray(variant.allocations) ? variant.allocations : [];
      const eligibleAllocations = allocations.filter(allocation => allocation?.id != null);
      const required = Boolean(variant.requires_selling_plan);
      const selectedPlan = eligibleAllocations.find(allocation => String(allocation.id) === String(preservePlanId));
      if (selectedPlan) this.currentPlanId = String(selectedPlan.id);
      else if (required && eligibleAllocations.length) this.currentPlanId = String(eligibleAllocations[0].id);

      if (!required) {
        const oneTime = this.makeChoice({
          value: 'one-time',
          label: this.dataset.oneTimeLabel || '',
          price: variant.price || this.getOneTimePrice(variantId),
          checked: !this.currentPlanId,
          type: 'one-time'
        });
        const oneTimeGroup = document.createElement('div');
        oneTimeGroup.className = 'maxler-purchase-options__group maxler-purchase-options__group--one-time';
        oneTimeGroup.append(oneTime);
        this.optionsRoot.append(oneTimeGroup);
      }

      if (eligibleAllocations.length) {
        const groupTitle = this.dataset.subscribeLabel || '';
        const group = document.createElement('div');
        group.className = 'maxler-purchase-options__group';
        const heading = document.createElement('p');
        heading.className = 'maxler-purchase-options__group-name';
        heading.textContent = groupTitle;
        group.append(heading);
        const list = document.createElement('div');
        list.className = 'maxler-purchase-options__choices';
        for (const allocation of eligibleAllocations) {
          list.append(this.makeChoice({
            value: 'subscription',
            planId: allocation.id,
            label: allocation.name || this.dataset.subscribeLabel || '',
            price: allocation.price,
            checked: String(allocation.id) === this.currentPlanId,
            type: 'subscription'
          }));
        }
        group.append(list);
        this.optionsRoot.append(group);
      }

      if (required && !eligibleAllocations.length) {
        this.status.textContent = this.dataset.unavailableText || '';
      } else {
        this.status.textContent = '';
      }
      this.requiredPlanMissing = required && !this.currentPlanId;
      this.syncFormPlan();
      this.updateSelectedStyles();
    }

    makeChoice({value, planId = '', label, price, checked, type}) {
      const row = document.createElement('label');
      row.className = `maxler-purchase-options__choice${checked ? ' is-selected' : ''}`;
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = `${this.id}-purchase-choice`;
      radio.value = value;
      radio.checked = checked;
      radio.dataset.purchaseMode = type;
      if (type === 'subscription') radio.dataset.planId = String(planId);
      radio.setAttribute('aria-label', price ? `${label}. ${price}` : label);
      const title = document.createElement('span');
      title.className = 'maxler-purchase-options__choice-title';
      title.textContent = label;
      row.append(radio, title);
      if (price) {
        const amount = document.createElement('span');
        amount.className = 'maxler-purchase-options__price';
        amount.textContent = price;
        row.append(amount);
      }
      return row;
    }

    getOneTimePrice(variantId) {
      const variantSelect = this.form.querySelector('[name="id"]');
      const variant = this.variantData.get(String(variantId));
      const price = variant?.price;
      if (price) return price;
      const selectedOption = variantSelect?.selectedOptions?.[0];
      return selectedOption?.textContent?.split(' - ').slice(1).join(' - ').trim() || '';
    }

    purchaseIsBlocked() {
      const variant = this.variantData.get(this.currentVariantId);
      const eligible = variant?.allocations?.some(allocation => String(allocation.id) === this.currentPlanId);
      return !variant || !variant.available || Boolean(variant.requires_selling_plan && !eligible) || Boolean(this.currentPlanId && !eligible);
    }

    onSubmit(event) {
      const formVariantId = this.form.querySelector('[name="id"]')?.value;
      if (String(formVariantId) !== this.currentVariantId || this.purchaseIsBlocked()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.status.textContent = this.dataset.unavailableText || '';
        this.syncFormPlan();
        return;
      }
      this.syncFormPlan();
    }

    syncFormPlan() {
      const variant = this.variantData.get(this.currentVariantId);
      const eligible = variant?.allocations?.some(allocation => String(allocation.id) === this.currentPlanId);
      this.planInput.value = eligible ? this.currentPlanId : '';
      this.planInput.disabled = !eligible || this.fieldset.disabled;
      this.requiredPlanMissing = Boolean(variant?.requires_selling_plan && !eligible);
      if (this.addButton) {
        if (this.purchaseIsBlocked()) {
          this.addButton.disabled = true;
          this.addButton.classList.add('disabled');
          this.addButton.dataset.maxlerPlanBlocked = 'true';
        } else if (this.addButton.dataset.maxlerPlanBlocked === 'true') {
          this.addButton.disabled = false;
          this.addButton.classList.remove('disabled');
          delete this.addButton.dataset.maxlerPlanBlocked;
        }
      }
    }

    updateSelectedStyles() {
      this.optionsRoot.querySelectorAll('.maxler-purchase-options__choice').forEach(row => {
        row.classList.toggle('is-selected', Boolean(row.querySelector('input')?.checked));
      });
    }
  }

  customElements.define('maxler-purchase-options', MaxlerPurchaseOptions);
})();
